import { useParams } from "react-router";
import { useOutletContext } from "react-router-dom";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Chip,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useState } from "react";

import Loader from "../../components/Loader";
import useGetPatientAppDocuments from "../../hooks/useGetAppDocuments";
import useDeleteAppDocumentMutation from "../../hooks/useDeleteAppDocumentMutation";
import useMoveAppDocumentMutation from "../../hooks/useMoveAppDocumentMutation";
import useGetAppPhotosSummary from "../../hooks/useGetAppPhotosSummary";
import useGetAppVideosSummary from "../../hooks/useGetAppVideosSummary";
import useGetSessionMoveTypes from "../../hooks/useGetSessionMoveTypes";
import { downloadSignedFile } from "../../utils/helpers";

const isImageUrl = (url) => {
  if (!url) return false;
  const path = url.split("?")[0];
  return /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i.test(path);
};

const MEDIA_TYPE_OPTIONS = [
  { value: "image", label: "Photo" },
  { value: "video", label: "Video" },
];

const PatientAppDocuments = () => {
  const { _id_ } = useParams();

  const { trialFilter, setActive, setBreadcrumbTitle, isTrialLocked } = useOutletContext();

  setActive("app-documents");
  setBreadcrumbTitle("App Documents");

  const { data: appDocuments = [], isLoading } = useGetPatientAppDocuments({
    trialId: trialFilter,
    patientId: _id_,
    enabled: !!trialFilter,
  });

  const { mutate: deleteAppDocument } = useDeleteAppDocumentMutation();
  const { mutate: moveAppDocument, isPending: isMoving } = useMoveAppDocumentMutation();

  // Delete dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);

  // View dialog state
  const [viewOpen, setViewOpen] = useState(false);
  const [viewDocument, setViewDocument] = useState({ url: "", fileName: "" });

  // Move dialog state
  const [moveDialog, setMoveDialog] = useState({ open: false, doc: null });
  const [selectedMediaType, setSelectedMediaType] = useState("");
  const [selectedSessionId, setSelectedSessionId] = useState("");
  const [selectedTypeId, setSelectedTypeId] = useState("");

  const isMoveImage = moveDialog.open && selectedMediaType === "image";
  const isMoveVideo = moveDialog.open && selectedMediaType === "video";

  const { data: photosSummary } = useGetAppPhotosSummary({
    patientId: _id_,
    trialId: trialFilter,
    enabled: isMoveImage && !!trialFilter,
  });

  const { data: videosSummary } = useGetAppVideosSummary({
    patientId: _id_,
    trialId: trialFilter,
    enabled: isMoveVideo && !!trialFilter,
  });

  const { data: moveTypes = [] } = useGetSessionMoveTypes({
    sessionId: selectedSessionId,
    mediaType: selectedMediaType,
    enabled: Boolean(selectedSessionId) && Boolean(selectedMediaType),
  });

  const moveSessions = isMoveImage
    ? photosSummary?.sessions || []
    : isMoveVideo
      ? videosSummary?.sessions || []
      : [];

  const handleViewClick = (url, fileName) => {
    setViewDocument({ url, fileName });
    setViewOpen(true);
  };

  const handleOpenDialog = (documentId) => {
    setSelectedDocumentId(documentId);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedDocumentId(null);
  };

  const handleDelete = (notifyUser) => {
    deleteAppDocument({ documentId: selectedDocumentId, patientId: _id_, notifyUser });
    handleCloseDialog();
  };

  const handleOpenMoveDialog = (doc) => {
    setMoveDialog({ open: true, doc });
    setSelectedMediaType("");
    setSelectedSessionId("");
    setSelectedTypeId("");
  };

  const handleCloseMoveDialog = () => {
    setMoveDialog({ open: false, doc: null });
    setSelectedMediaType("");
    setSelectedSessionId("");
    setSelectedTypeId("");
  };

  const handleMove = () => {
    if (!selectedMediaType || !selectedSessionId || !selectedTypeId) return;
    moveAppDocument(
      {
        documentId: moveDialog.doc.id,
        mediaType: selectedMediaType,
        photoSessionId: selectedSessionId,
        typeId: selectedTypeId,
      },
      { onSuccess: handleCloseMoveDialog }
    );
  };

  const headerCellSx = {
    backgroundColor: (theme) => theme.palette.grey[100],
    fontWeight: "bold",
    textTransform: "uppercase",
    fontSize: "0.85rem",
    color: "#424242",
    whiteSpace: "nowrap",
  };

  const bodyCellSx = { whiteSpace: "nowrap" };

  return (
    <Paper elevation={1} sx={{ p: 2 }}>
      <Stack spacing={1} mb={2}>
        <Typography variant="h6">App Documents</Typography>
      </Stack>

      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: "80vh" }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx}>Document Name</TableCell>
              <TableCell sx={headerCellSx}>Upload Time</TableCell>
              <TableCell sx={headerCellSx}>Status</TableCell>
              <TableCell sx={headerCellSx}>Action</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <Loader />
                </TableCell>
              </TableRow>
            )}

            {!isLoading && appDocuments.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No app documents available</Typography>
                </TableCell>
              </TableRow>
            )}

            {!isLoading &&
              appDocuments.map((doc) => (
                <TableRow key={doc.id} hover>
                  <TableCell sx={bodyCellSx}>{doc.description || "-"}</TableCell>

                  <TableCell sx={bodyCellSx}>
                    {doc.created_at ? new Date(doc.created_at + "Z").toLocaleString() : "-"}
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    {doc.is_moved ? (
                      <Chip label="Moved" color="success" size="small" />
                    ) : (
                      <Chip label="Pending" color="warning" size="small" variant="outlined" />
                    )}
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    <Stack direction="row" spacing={1}>
                      <Link
                        component="button"
                        underline="hover"
                        onClick={() =>
                          handleViewClick(doc.document, doc.description || `document-${doc.id}`)
                        }
                      >
                        View
                      </Link>

                      <Typography component="span" color="text.secondary">
                        /
                      </Typography>

                      <Link
                        component="button"
                        underline="hover"
                        onClick={() =>
                          downloadSignedFile(doc.document, doc.description || `document-${doc.id}`)
                        }
                      >
                        Download
                      </Link>

                      {!doc.is_moved && !isTrialLocked && (
                        <>
                          <Typography component="span" color="text.secondary">
                            /
                          </Typography>
                          <Link
                            component="button"
                            underline="hover"
                            onClick={() => handleOpenMoveDialog(doc)}
                          >
                            Move
                          </Link>
                        </>
                      )}

                      {!isTrialLocked && (
                        <>
                          <Typography component="span" color="text.secondary">
                            /
                          </Typography>
                          <Link
                            component="button"
                            underline="hover"
                            onClick={() => handleOpenDialog(doc.id)}
                          >
                            Delete
                          </Link>
                        </>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Document Viewer Dialog */}
      <Dialog
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { height: "90vh" } }}
      >
        <DialogTitle
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1 }}
        >
          <Typography variant="subtitle1" noWrap sx={{ maxWidth: "85%" }}>
            {viewDocument.fileName}
          </Typography>
          <IconButton size="small" onClick={() => setViewOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column" }}>
          {isImageUrl(viewDocument.url) ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 2,
                bgcolor: "grey.50",
              }}
            >
              <img
                src={viewDocument.url}
                alt={viewDocument.fileName}
                style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
              />
            </Box>
          ) : (
            <iframe
              src={viewDocument.url}
              title={viewDocument.fileName}
              style={{ flex: 1, width: "100%", border: "none" }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Delete Document</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this document?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
          <Button onClick={() => handleDelete(false)}>Delete</Button>
          <Button onClick={() => handleDelete(true)}>Delete & Notify User</Button>
        </DialogActions>
      </Dialog>

      {/* Move Dialog */}
      <Dialog open={moveDialog.open} onClose={handleCloseMoveDialog} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          Move Document
          <IconButton size="small" onClick={handleCloseMoveDialog}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={3} mt={1}>
            <FormControl size="small" fullWidth>
              <InputLabel>Media Type</InputLabel>
              <Select
                label="Media Type"
                value={selectedMediaType}
                onChange={(e) => {
                  setSelectedMediaType(e.target.value);
                  setSelectedSessionId("");
                  setSelectedTypeId("");
                }}
              >
                {MEDIA_TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedMediaType && (
              <FormControl size="small" fullWidth>
                <InputLabel>Visit</InputLabel>
                <Select
                  label="Visit"
                  value={selectedSessionId}
                  onChange={(e) => {
                    setSelectedSessionId(e.target.value);
                    setSelectedTypeId("");
                  }}
                >
                  {moveSessions.length === 0 && (
                    <MenuItem disabled value="">
                      No sessions available
                    </MenuItem>
                  )}
                  {moveSessions.map((s) => (
                    <MenuItem key={s.session_id} value={s.session_id}>
                      {s.visit_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {selectedSessionId && (
              <FormControl size="small" fullWidth>
                <InputLabel>{isMoveImage ? "Photo Type" : "Video Type"}</InputLabel>
                <Select
                  label={isMoveImage ? "Photo Type" : "Video Type"}
                  value={selectedTypeId}
                  onChange={(e) => setSelectedTypeId(e.target.value)}
                >
                  {moveTypes.length === 0 && (
                    <MenuItem disabled value="">
                      No types available
                    </MenuItem>
                  )}
                  {moveTypes.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseMoveDialog} disabled={isMoving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleMove}
            disabled={!selectedMediaType || !selectedSessionId || !selectedTypeId || isMoving}
            startIcon={isMoving ? <CircularProgress size={16} /> : null}
          >
            {isMoving ? "Moving..." : "Move"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default PatientAppDocuments;
